import { PropsWithChildren, useEffect, useState } from 'react';

import Consts from 'Interfaces/Consts';
import { Filter } from 'Interfaces/general.interface';

type Props = {
    componentId: string;
    filters: (Filter | null)[];
    filterValues: string[];
    updateFilterValues: (filters: any[]) => void;
    offcanvasIsOpen: boolean;
    toggleOffcanvas: () => void;
}

const SearchAndFilterOffcanvas: React.FC<Props> = (props) => {

    // In order for changes inside the offcanvas to not effect the parent's filters,
    // the offcanvas manages its own filters state.
    // Changes will be executed only if user submit the form.
    // if the user close the offcanvas - changes will not take effect.
    const [filterValues, setFilterValues] = useState<string[]>([...props.filterValues]);
    const { filters, componentId, toggleOffcanvas, offcanvasIsOpen } = props;

    useEffect(() => {
        // Set the state filters to the parent component's filters
        // every time the offcanvas opens
        if (offcanvasIsOpen) {
            initSyncFilters();
            focusFirstDropdown();
        }
    }, [offcanvasIsOpen]);


    const initSyncFilters = () => setFilterValues([...props.filterValues]);

    const onSubmit = (e) => {
        e.preventDefault();
        props.updateFilterValues(filterValues);
        props.toggleOffcanvas();
    }

    const updateOffcanvasFilterValue = (e, filter_value: any, filterIdx: number) => {
        e.preventDefault();
        const updatedFilters = [...filterValues];
        updatedFilters[filterIdx] = filter_value;
        setFilterValues(updatedFilters);
    }

    const clearAllFilters = () => { setFilterValues(["", "", "", "", ""]) };

    const focusFirstDropdown = () => {
        const formElement = document.getElementById(`saf-offcanvas-${props.componentId}`);
        const dropdown = formElement.querySelector('.dropdown-toggle')
        if (dropdown) {
            (dropdown as HTMLElement).focus()
        }
    }

    return (

        <div className={`offcanvas-filters-wrapper d-flex justify-content-end ${offcanvasIsOpen ? 'show' : 'hide'}`}>

            <div className="offcanvas-inner">

                <form id={`saf-offcanvas-${componentId}`} onSubmit={onSubmit} className="offcanvas-container">

                    <CloseBtn onClose={toggleOffcanvas} />

                    <Title />

                    <div className="filters-container">

                        {
                            filters.map((item, filterIdx) => (
                                !!item ?
                                    <FilterDropdown key={'selectFilter_' + filterIdx}>

                                        <FilterDropdown.Button filterName={filters[filterIdx].filterName} filterValue={filterValues[filterIdx]} />

                                        <FilterDropdown.Menu>
                                            {
                                                item.filterValues.map((filter_value, filterValueIdx) => (
                                                    <FilterDropdown.MenuOption
                                                        isSelectedValue={filter_value === filterValues[filterIdx]}
                                                        filterIdx={filterIdx}
                                                        filterValue={filter_value}
                                                        onSelect={updateOffcanvasFilterValue}
                                                        key={filter_value + filterValueIdx}
                                                    />
                                                ))
                                            }
                                        </FilterDropdown.Menu>

                                    </FilterDropdown>
                                    :
                                    <EmptyFilter key={'selectFilter_' + filterIdx} />
                            ))
                        }

                        <SubmitBtn />
                                
                    </div>

                    <ClearAllBtn onClick={clearAllFilters} />

                </form>

            </div>

        </div>

    );
    
}

export default SearchAndFilterOffcanvas;


const CloseBtn = ({ onClose }: { onClose: () => void }) => {
    return <button type="button" className="close-btn p-0" aria-label="Close" onClick={onClose}>
        <i className="icon-minisite-close minisite-font-36" aria-hidden="true"></i>
    </button>
}

const Title = () => <h3 className="offcanvas-title">{Consts.selectFilter}</h3>;

const EmptyFilter = () => <div style={{ display: 'none' }}></div>;

const FilterDropdown = ({ children }: PropsWithChildren) => {
    return <div className={`filter-input-wrapper mobile-dropdown dropdown-bottom-border dropdown`}> {children} </div>
}

FilterDropdown.Menu = ({ children }: PropsWithChildren) => {
    return <div className="dropdown-menu mobile-dropdown-content shadow col-12 border-0"> {children}</div>
}

FilterDropdown.MenuOption = ({ isSelectedValue, filterIdx, filterValue, onSelect }: { isSelectedValue: boolean; filterValue: string; filterIdx: number, onSelect: (e: any, value: string, idx: number) => void }) => {
    return <div>
        <a
            href="#"
            className={`dropdown-item overflow-elipsis ws-normal ${isSelectedValue ? 'dropdown-selected-bg' : ''}`}
            style={{ textDecoration: 'none' }}
            onClick={(e) => { onSelect(e, filterValue, filterIdx) }}>
            {
                isSelectedValue &&
                <i className="fa fa-check" style={{ fontSize: '12px' }} aria-hidden="true"></i>
            }
            <span className={isSelectedValue ? 'px-2' : ''}>{filterValue}</span>
        </a>
    </div>
}

FilterDropdown.Button = ({ filterName, filterValue }: { filterValue: string; filterName: string }) => {
    return <button
        type="button"
        className="dropdown-toggle d-flex justify-content-between align-items-center bg-transparent component-search-filter align-self-center"
        data-bs-toggle="dropdown"
        data-toggle="dropdown"
        aria-expanded="false">
        <span className="dropdown-mobile-title overflow-elipsis max-mobile-w-dropdown">{filterValue || filterName}</span>

        <svg className="dropdown-arrow txt-pallete-color" aria-hidden="true" width="11" height="7" viewBox="0 0 11 7" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M5.09156 6.45417C4.83556 6.45417 4.57956 6.35617 4.38456 6.16117L0.384559 2.16117C-0.00644141 1.77017 -0.00644141 1.13817 0.384559 0.747168C0.775559 0.356168 1.40756 0.356168 1.79856 0.747168L5.10356 4.05217L8.39656 0.872168C8.79556 0.489168 9.42656 0.500168 9.81056 0.897168C10.1946 1.29417 10.1836 1.92817 9.78656 2.31117L5.78656 6.17317C5.59156 6.36117 5.34156 6.45417 5.09156 6.45417Z" fill="currentColor" />
        </svg>
    </button>
}

const SubmitBtn = () => <button className="offcanvas-submit-btn" type="submit">{Consts.showResults}</button>;

const ClearAllBtn = ({ onClick }: { onClick: () => void }) => {
    return <button type="button" onClick={onClick}
        className="txt-pallete-color clear-all-mobile-btn offcanvas-submit-btn border-0 mt-1">
        {Consts.clearEverything}
    </button>
}
